import React, { useState } from "react";

/** Rex Counsel palette */
const COLORS = {
  pale: "#95B4CC",
  accent: "#6B8BA4",
  primary: "#2B4C65",
  ink: "#1D1C22",
  white: "#FFFFFF",
};

const SPOTLIGHT_COPY = `Rex Counsel LLP is more than a law firm — it is a global legal bridge between India, the United Kingdom, and the United States. We specialize in crafting cross-border strategies that align business, technology, and human rights with precision and foresight. Our practice spans corporate advisory, international arbitration, immigration and asylum law, cyber and data protection, as well as NRI succession and asset management. With a forward-looking approach, we transform legal complexities into opportunities, ensuring our clients — whether multinational corporations, dynamic entrepreneurs, or individuals navigating personal transitions — receive counsel that is strategic, bespoke, and globally attuned. At Rex Counsel LLP, we redefine advocacy without borders.`;

export default function SpotlightSection() {
  const [expanded, setExpanded] = useState(false);

  const teaserChars = 420; // adjust if you want a shorter/longer preview
  const isLong = SPOTLIGHT_COPY.length > teaserChars;
  const preview = isLong ? SPOTLIGHT_COPY.slice(0, teaserChars).trim() + "…" : SPOTLIGHT_COPY;

  return (
    <section aria-label="Spotlight" className="relative py-10 sm:py-12">
      {/* subtle background wash + watermark */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(149,180,204,0.07), rgba(107,139,164,0.05), rgba(255,255,255,0))",
          }}
        />
        <div className="absolute inset-y-0 right-[-6vw] flex items-center justify-center pointer-events-none select-none">
          <div
            className="text-[22rem] leading-none"
            style={{ color: COLORS.primary, opacity: 0.035 }}
            aria-hidden
          >
            ⚖
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* heading */}
        <div className="mb-5">
          <div className="text-[11px] tracking-widest uppercase text-slate-500">Spotlight</div>
          <div
            className="h-px mt-2 bg-gradient-to-r from-[var(--pale)] via-[var(--accent)] to-transparent"
            style={{ "--pale": COLORS.pale, "--accent": COLORS.accent }}
          />
        </div>

        {/* gradient frame card */}
        <div
          className="rounded-3xl p-[1px]"
          style={{
            background: `linear-gradient(135deg, ${COLORS.pale}, ${COLORS.accent})`,
          }}
        >
          <div className="rounded-3xl bg-white/90 backdrop-blur-sm p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-semibold mb-3" style={{ color: COLORS.primary }}>
              Rex Counsel LLP
            </h3>

            <p className="text-[15px] leading-7 text-slate-800">
              {expanded ? SPOTLIGHT_COPY : preview}
            </p>

            {isLong && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl"
                style={{
                  background: expanded ? COLORS.primary : COLORS.pale,
                  color: expanded ? COLORS.white : COLORS.primary,
                }}
              >
                {expanded ? "Show less" : "Read more"}
              </button>
            )}

            {/* focus chips */}
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                "Corporate Advisory",
                "International Arbitration",
                "Immigration & Asylum",
                "Cyber & Data Protection",
                "NRI Succession & Assets",
              ].map((chip) => (
                <span
                  key={chip}
                  className="text-xs px-2.5 py-1 rounded-full"
                  style={{ background: COLORS.pale, color: COLORS.primary }}
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
